import { useNavigate, useParams, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import { useEffect, useMemo, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { useSelector } from "react-redux";
import { stripHtml } from "../../../../components/Utils";
import { PMField } from "../../../../components/utils/PMField";
import { FormikSaveBar } from "../../../../components/utils/FormikSaveBar";
import { ResourceLoading } from "../../../../components/utils/ResourceLoading";
import DashboardHeader from "../../../../components/dashboard/dashboardHeader";
import { ProjectSelector } from "../../../../components/utils/moduleSelectors/projectSelector";
import { CustomerSelector } from "../../../../components/utils/moduleSelectors/customerSelector";
import {
  fetchDeviation,
  createDeviation as createDeviationHttp,
  updateDeviation as updateDeviationHttp,
} from "../../../../api/quality/deviation";
import {
  getDeviationTypeOptions,
  priorityOptions,
  severityOptions,
} from "../../../../components/dashboard/quality/deviations/utils";
import { fetchUsers } from "../../../../api/user";
import { ProjectFolderSelector } from "../../../../components/utils/moduleSelectors/projectFolderSelector";
import { PMDatePicker } from "../../../../components/utils/PMDatePicker";
import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { createPortal } from "react-dom";
import { WorkOrderSelector } from "../../../../components/utils/moduleSelectors/workOrderSelector";
import { addMinutes, format } from "date-fns";
import {
  createWorkOrder,
  isPromangApiErrorResponse,
  promangApiErrorsMessage,
} from "../../../../api/work-order";
import { createEvent } from "../../../../api/events";

/** Avvik-nummer og tittel som forslag til AO — samme tekstnykler som work order-skolen. */
function formatSuggestedWorkOrderTitleFromDeviationValues(values, deviationRecord, t) {
  const label = (
    deviationRecord?.displayTitle?.trim?.() ||
    values.title?.trim?.() ||
    ""
  ).trim();
  const num =
    deviationRecord?.number ??
    values.number ??
    "";
  const numStr = num !== null && String(num).trim() !== "" ? String(num).trim() : "";
  if (numStr && label) {
    return t("deviation.work_order.suggested_wo_title_full", {
      number: numStr,
      title: label,
    });
  }
  if (label) return label;
  if (numStr) {
    return t("deviation.work_order.suggested_wo_title_number_only", {
      number: numStr,
    });
  }
  return "";
}

function roundUpToNearestHalfHour(d) {
  const date = d instanceof Date ? new Date(d) : new Date(d);
  if (Number.isNaN(date.getTime())) return new Date();
  date.setSeconds(0, 0);
  const minutes = date.getMinutes();
  const remainder = minutes % 30;
  if (remainder !== 0) {
    date.setMinutes(minutes + (30 - remainder));
  }
  return date;
}

function valuesToDeviationApiBody(values) {
  const {
    submit: _submit,
    work_order_link_mode,
    work_order_create_timing: _timing,
    work_order_executor_user_id: _executor,
    ...rest
  } = values;
  let work_order_id =
    typeof values.work_order_id === "string" && values.work_order_id.trim() !== ""
      ? Number(values.work_order_id) || values.work_order_id
      : values.work_order_id ?? null;

  if (work_order_link_mode === "none" || work_order_link_mode === "create") {
    work_order_id = null;
  }
  return { ...rest, work_order_id };
}

export const EditDeviation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const { t } = useTranslation();
  const currentUser = useSelector((state) => state.session.currentUser);
  const isAdmin = currentUser?.roles?.some((r) => r.name === "admin");

  const [isLoading, setIsLoading] = useState(true);
  const [deviation, setDeviation] = useState(null);
  const [deviationTypeOptions, setDeviationTypeOptions] = useState([]);
  const formikRef = useRef(null);
  const [showSeverityDialog, setShowSeverityDialog] = useState(false);
  const [showPriorityDialog, setShowPriorityDialog] = useState(false);
  const [showDeviationTypeDialog, setShowDeviationTypeDialog] = useState(false);
  const [showResponsibleUserSelector, setShowResponsibleUserSelector] = useState(false);
  const [showExecutorUserSelector, setShowExecutorUserSelector] = useState(false);
  const [executorUserSearch, setExecutorUserSearch] = useState("");
  const [deviationTypeSearch, setDeviationTypeSearch] = useState("");
  const titleInputRef = useRef(null);
  const [employeeState, setEmployeeState] = useState({
    isLoading: false,
    data: [],
  });
  const [employeeSearch, setEmployeeSearch] = useState("");

  const [project, setProject] = useState(
    location?.state?.project || null
  );
  const [customer, setCustomer] = useState(
    location?.state?.customer || project?.customer || null
  );

  const getDeviation = async () => {
    if (!id) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetchDeviation(id);
      if (response?.data?.id) {
        setDeviation(response.data);
        if (response.data.project && !project) {
          setProject(response.data.project);
        }
        if (response.data.customer && !customer) {
          setCustomer(response.data.customer);
        }
      }
    } catch (err) {
      toast.error(t("deviation.toast.load_failed"));
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    async function fetchData() {
      const fetchedOptions = await getDeviationTypeOptions();
      setDeviationTypeOptions(fetchedOptions);
    }
    async function fetchEmployees() {
      setEmployeeState((prev) => ({ ...prev, isLoading: true }));
      try {
        const response = await fetchUsers({
          filters: [{ name: "role", value: "deviations" }],
          page: 1,
          per_page: 50,
          query: "",
          sort: "asc",
          sortBy: "name",
          view: "all",
          fields: "id,name,displayTitle,email",
        });
        setEmployeeState({
          isLoading: false,
          data: Array.isArray(response?.data) ? response.data : [],
        });
      } catch (_error) {
        setEmployeeState({ isLoading: false, data: [] });
      }
    }
    fetchData();
    fetchEmployees();
  }, []);

  useEffect(() => {
    if (id) {
      getDeviation();
    } else {
      setIsLoading(false);
    }
  }, [id]);

  // Focus title input when page loads
  useEffect(() => {
    if (!isLoading && titleInputRef.current) {
      setTimeout(() => {
        titleInputRef.current?.focus();
      }, 100);
    }
  }, [isLoading]);

  const workOrderPickFilters = useMemo(
    () =>
      deviation?.id
        ? [
            {
              name: "without_deviation",
              value: true,
              except_deviation_id: deviation.id,
            },
          ]
        : [],
    [deviation?.id]
  );

  const filteredExecutorEmployees = useMemo(() => {
    const query = executorUserSearch.trim().toLowerCase();
    if (!query) return employeeState.data;
    return employeeState.data.filter((employee) => {
      const haystack = [
        employee?.displayTitle,
        employee?.name,
        employee?.email,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [executorUserSearch, employeeState.data]);

  const filteredEmployees = useMemo(() => {
    const query = employeeSearch.trim().toLowerCase();
    if (!query) return employeeState.data;
    return employeeState.data.filter((employee) => {
      const haystack = [
        employee?.displayTitle,
        employee?.name,
        employee?.email,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [employeeSearch, employeeState.data]);

  /** Stable reference so enableReinitialize does not wipe in-progress edits on unrelated re-renders (e.g. opening pickers). */
  const initialValues = useMemo(
    () => ({
      id: deviation?.id || null,
      title: deviation?.title || "",
      description: deviation?.description || "",
      severity: deviation?.severity || "1",
      priority: deviation?.priority || "1",
      reference: deviation?.reference || "",
      customer_id: deviation?.customer?.id || customer?.id,
      project_id: deviation?.project?.id || project?.id,
      deviation_type_id: deviation?.deviation_type?.id || null,
      deviation_date:
        deviation?.date || new Date().toISOString().split("T")[0],
      proposed_measures: deviation?.proposed_measures || "",
      number: deviation?.number || "",
      responsible_user_id: deviation?.responsible_user?.id || currentUser?.id,
      assigned_user_id: deviation?.assigned_user?.id || null,
      work_order_id: deviation?.work_order?.id || null,
      folder_id: deviation?.folder?.id || null,
      work_order_link_mode: deviation?.work_order?.id ? "existing" : "none",
      work_order_create_timing: "perform_now",
      work_order_executor_user_id:
        deviation?.assigned_user?.id ??
        deviation?.responsible_user?.id ??
        currentUser?.id,
      submit: null,
    }),
    [
      deviation,
      customer?.id,
      project?.id,
      currentUser?.id,
    ]
  );

  const filteredDeviationTypeOptions = useMemo(() => {
    const q = deviationTypeSearch.trim().toLowerCase();
    if (!q) return deviationTypeOptions;
    return deviationTypeOptions.filter((opt) =>
      String(opt.title || "")
        .toLowerCase()
        .includes(q)
    );
  }, [deviationTypeSearch, deviationTypeOptions]);

  /** Do not nest `const Inner = () => <Formik/>` inside this component — each parent re-render
   *  creates a new function type and React remounts Formik (wiping picks from portals). */

  return (
    <>
      <DashboardHeader
        subtitle=""
        title={deviation?.id ? t("deviation.form.edit_deviation") : t("deviation.form.create_deviation")}
        meta={[]}
      >
        {isLoading ? (
          <ResourceLoading />
        ) : (
          <Formik
            enableReinitialize={true}
            initialValues={initialValues}
            validationSchema={Yup.object({
              deviation_type_id: Yup.string().required(
                "Deviation type is required"
              ),
            })}
            onSubmit={async (values, helpers) => {
              const apiBody = valuesToDeviationApiBody(values);
              const effectiveProject = deviation?.project || project || null;
              const effectiveCustomer =
                deviation?.customer ||
                customer ||
                effectiveProject?.customer ||
                null;
              const projectId =
                apiBody.project_id || effectiveProject?.id || null;
              const customerId =
                apiBody.customer_id ||
                effectiveCustomer?.id ||
                effectiveProject?.customer?.id ||
                null;

              if (
                values.work_order_link_mode === "create" &&
                (!projectId || !customerId)
              ) {
                toast.error(
                  t("deviation.form.need_project_customer_for_work_order")
                );
                return;
              }

              helpers.setSubmitting(true);

              try {
                const existingId = deviation?.id || id || null;

                let saveResult;
                if (existingId) {
                  saveResult = await updateDeviationHttp({
                    ...apiBody,
                    id: Number(existingId),
                  });
                } else {
                  saveResult = await createDeviationHttp(apiBody);
                }

                const saveFailed =
                  saveResult?.isAxiosError ||
                  (saveResult?.response?.status ?? 0) >= 400 ||
                  (Array.isArray(saveResult?.errors) &&
                    saveResult.errors.length);

                if (saveFailed) {
                  const msg =
                    (saveResult?.isAxiosError &&
                      promangApiErrorsMessage(
                        saveResult.response,
                        saveResult.message
                      )) ||
                    (Array.isArray(saveResult?.errors)
                      ? saveResult.errors.join(", ")
                      : null) ||
                    t("deviation.form.save_failed");
                  toast.error(msg);
                  helpers.setSubmitting(false);
                  return;
                }

                let savedDeviation =
                  saveResult?.data && typeof saveResult.data === "object"
                    ? saveResult.data
                    : saveResult;
                if (
                  savedDeviation?.data &&
                  typeof savedDeviation.data === "object" &&
                  savedDeviation.data.id
                ) {
                  savedDeviation = savedDeviation.data;
                }

                const devId =
                  savedDeviation?.id || (existingId ? Number(existingId) : null);

                if (!devId) {
                  toast.error(t("deviation.form.save_failed"));
                  helpers.setSubmitting(false);
                  return;
                }

                const goPlanLater =
                  values.work_order_link_mode === "create" &&
                  values.work_order_create_timing === "plan_later";

                if (goPlanLater) {
                  helpers.setSubmitting(false);
                  toast.success(t("deviation.form.saved"));
                  navigate("/dashboard/work_orders/new", {
                    state: {
                      customer:
                        effectiveCustomer ||
                        effectiveProject?.customer ||
                        customer,
                      project:
                        savedDeviation?.project ||
                        effectiveProject ||
                        project,
                      delivery_address:
                        savedDeviation?.project?.delivery_address ||
                        effectiveProject?.delivery_address,
                      return_after_work_order_create: {
                        deviation_id: devId,
                      },
                      work_order_title_from_deviation: {
                        number: savedDeviation?.number ?? apiBody.number,
                        displayTitle:
                          savedDeviation?.displayTitle ||
                          `${apiBody.reference || ""} ${apiBody.title || ""}`
                            .trim(),
                        title: savedDeviation?.title ?? apiBody.title,
                      },
                    },
                  });
                  return;
                }

                const goPerformNow =
                  values.work_order_link_mode === "create" &&
                  values.work_order_create_timing === "perform_now";

                if (goPerformNow) {
                  const suggestedTitle =
                    formatSuggestedWorkOrderTitleFromDeviationValues(
                      values,
                      savedDeviation ?? deviation,
                      t
                    ).trim() || stripHtml(values.title || "");

                  const descriptionPlain = stripHtml(values.title || "") || "";

                  const execIdRaw =
                    values.work_order_executor_user_id || currentUser?.id;

                  const participants = [];
                  if (
                    execIdRaw &&
                    String(execIdRaw) !== String(currentUser?.id)
                  ) {
                    participants.push({
                      user_id:
                        typeof execIdRaw === "string"
                          ? Number(execIdRaw) || execIdRaw
                          : execIdRaw,
                      role: "RESPONSIBLE_EXECUTOR",
                    });
                  }

                  const woPayload = {
                    title:
                      suggestedTitle ||
                      t("deviation.form.default_wo_title"),
                    description: descriptionPlain,
                    project_id: Number(projectId),
                    customer_id: Number(customerId),
                    delivery_address_id:
                      savedDeviation?.project?.delivery_address?.id ??
                      effectiveProject?.delivery_address?.id ??
                      null,
                    order_date:
                      values.deviation_date ||
                      new Date().toISOString().split("T")[0],
                    due_date:
                      values.deviation_date ||
                      new Date().toISOString().split("T")[0],
                    participants,
                  };

                  let woRes;
                  try {
                    woRes = await createWorkOrder(woPayload, {
                      params: { fields: "*" },
                    });
                  } catch (e) {
                    woRes = e?.response ?? e;
                  }

                  if (!woRes || isPromangApiErrorResponse(woRes)) {
                    toast.error(
                      promangApiErrorsMessage(
                        woRes,
                        t("deviation.form.work_order_create_failed")
                      )
                    );
                    helpers.setSubmitting(false);
                    if (!existingId) {
                      navigate(`/dashboard/hse-ks/deviations/${devId}`);
                    } else {
                      getDeviation();
                    }
                    return;
                  }

                  const woData = woRes?.data?.data ?? woRes?.data;
                  const woId = woData?.id;

                  if (!woId) {
                    toast.error(t("deviation.form.work_order_create_failed"));
                    helpers.setSubmitting(false);
                    return;
                  }

                  const linkPayload = await updateDeviationHttp({
                    ...apiBody,
                    id: devId,
                    work_order_id: woId,
                  });

                  const linkFailed =
                    linkPayload?.isAxiosError ||
                    (linkPayload?.response?.status ?? 0) >= 400 ||
                    (Array.isArray(linkPayload?.errors) &&
                      linkPayload.errors.length);

                  if (linkFailed) {
                    toast.error(t("deviation.work_order.link_failed_after_create"));
                    helpers.setSubmitting(false);
                    return;
                  }

                  const execId =
                    typeof execIdRaw === "string"
                      ? Number(execIdRaw) || execIdRaw
                      : execIdRaw;

                  const start = roundUpToNearestHalfHour(new Date());
                  const end = addMinutes(start, 120);

                  try {
                    const evRes = await createEvent({
                      title: woPayload.title,
                      description: woPayload.description,
                      start_time: format(start, "yyyy-MM-dd'T'HH:mm"),
                      end_time: format(end, "yyyy-MM-dd'T'HH:mm"),
                      all_day: false,
                      location: "",
                      project_id: Number(projectId),
                      module: "work_order",
                      module_id: woId,
                      user_id: execId,
                      participants: [
                        {
                          user_id: execId,
                          role: "PARTICIPANT",
                          status: "ASSIGNED",
                          notes: "",
                        },
                      ],
                    });

                    const status = evRes?.status ?? 200;
                    const evBody = evRes?.data ?? {};
                    const evErrs = evBody?.errors;
                    const evBad =
                      status >= 400 ||
                      (Array.isArray(evErrs) && evErrs.length);

                    if (evBad) {
                      toast.error(
                        Array.isArray(evErrs) && evErrs.length
                          ? `${t("deviation.form.event_creation_failed")}: ${evErrs.join(", ")}`
                          : t("deviation.form.event_creation_failed")
                      );
                    }
                  } catch (_ev) {
                    toast.error(t("deviation.form.event_creation_failed"));
                  }

                  toast.success(t("deviation.form.work_order_and_event_saved"));
                  helpers.setSubmitting(false);
                  helpers.setStatus({ success: true });
                  if (!existingId) {
                    navigate(`/dashboard/hse-ks/deviations/${devId}`);
                  } else {
                    getDeviation();
                  }
                  return;
                }

                helpers.setStatus({ success: true });
                helpers.setSubmitting(false);
                toast.success(t("deviation.form.saved"));
                if (!existingId) {
                  navigate(`/dashboard/hse-ks/deviations/${devId}`);
                } else {
                  getDeviation();
                }
              } catch (err) {
                console.error(err);
                helpers.setStatus({ success: false });
                helpers.setErrors({ submit: err.message });
                toast.error(err.message || t("deviation.form.save_failed"));
                helpers.setSubmitting(false);
              }
            }}
          >
            {(formik) => {
              formikRef.current = formik;
              const selectedResponsibleUser = employeeState.data.find(
                (employee) =>
                  String(employee.id) ===
                  String(formik.values.responsible_user_id)
              );
              const selectedExecutorUser = employeeState.data.find(
                (employee) =>
                  String(employee.id) ===
                  String(formik.values.work_order_executor_user_id)
              );

              return (
                <Form>
                  <div className="mx-auto mt-8 grid max-w-3xl grid-cols-1 gap-6 sm:px-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-3">
                    <div className="space-y-6 lg:col-span-3 lg:col-start-1 sm:col-span-3">
                      <div className="mt-5 shadow sm:overflow-hidden sm:rounded-md">
                        <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
                          <div className="grid grid-cols-6 gap-6">
                            <div className="col-span-6 sm:col-span-6">
                              <PMField
                                name="title"
                                type="text"
                                label={t("common.title")}
                                ref={titleInputRef}
                                autoFocus
                                {...formik}
                              />
                            </div>
                            <div className="col-span-6">
                              <PMField
                                name="description"
                                label={t("deviation.description.title")}
                                multiline
                                rows={5}
                                placeholder={t("deviation.description.placeholder")}
                                {...formik}
                              />
                              <p className="mt-2 text-sm text-slate-500">
                                {t("deviation.description.help")}
                              </p>
                            </div>

                            <div className="col-span-6 sm:col-span-3">
                              <PMDatePicker
                                name="deviation_date"
                                label={t("deviation.form.date")}
                                value={formik.values.deviation_date}
                                onChange={(e) => {
                                  formik.setFieldValue(
                                    "deviation_date",
                                    e.target.value
                                  );
                                }}
                                errors={formik.errors.deviation_date}
                                touched={formik.touched.deviation_date}
                              />
                            </div>

                            {!customer?.id && (
                              <div className="col-span-6 sm:col-span-3">
                                <CustomerSelector
                                  selected={
                                    deviation?.customer?.id
                                      ? deviation?.customer
                                      : customer
                                  }
                                  {...formik}
                                />
                              </div>
                            )}

                            {!project?.id && (
                              <div className="col-span-6 sm:col-span-3">
                                <ProjectSelector
                                  selected={
                                    deviation?.project?.id
                                      ? deviation?.project
                                      : project
                                  }
                                  {...formik}
                                />
                              </div>
                            )}

                            {isAdmin && (
                              <div className="col-span-6 sm:col-span-3">
                                <ProjectFolderSelector
                                  projectId={
                                    formik.values?.project_id || project?.id
                                  }
                                  selected={
                                    deviation?.folder?.id
                                      ? deviation?.folder
                                      : null
                                  }
                                  {...formik}
                                />
                              </div>
                            )}

                            <div className="col-span-6 sm:col-span-6 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                              <label className="mb-3 block text-sm font-medium text-slate-800">
                                {t("deviation.form.work_order_heading")}
                              </label>
                              <div className="flex flex-wrap gap-2">
                                {[
                                  {
                                    key: "none",
                                    labelKey:
                                      "deviation.form.work_order_mode_none",
                                  },
                                  {
                                    key: "existing",
                                    labelKey:
                                      "deviation.form.work_order_mode_existing",
                                  },
                                  {
                                    key: "create",
                                    labelKey:
                                      "deviation.form.work_order_mode_create",
                                  },
                                ].map(({ key, labelKey: lk }) => (
                                  <button
                                    key={key}
                                    type="button"
                                    onClick={() => {
                                      formik.setFieldValue(
                                        "work_order_link_mode",
                                        key
                                      );
                                      if (key !== "existing") {
                                        formik.setFieldValue(
                                          "work_order_id",
                                          null
                                        );
                                      }
                                      if (key === "create") {
                                        formik.setFieldValue(
                                          "work_order_create_timing",
                                          "perform_now"
                                        );
                                      }
                                    }}
                                    className={`rounded-full px-3 py-1.5 text-sm font-medium transition ${
                                      formik.values.work_order_link_mode ===
                                      key
                                        ? "bg-blue-600 text-white shadow-sm"
                                        : "border border-slate-200 bg-white text-slate-700 hover:bg-slate-100"
                                    }`}
                                  >
                                    {t(lk)}
                                  </button>
                                ))}
                              </div>

                              {formik.values.work_order_link_mode ===
                                "existing" && (
                                <div className="mt-4">
                                  <WorkOrderSelector
                                    selected={
                                      formik.values.work_order_id &&
                                      String(
                                        deviation?.work_order?.id ?? ""
                                      ) === String(formik.values.work_order_id)
                                        ? deviation.work_order
                                        : formik.values.work_order_id
                                          ? {
                                              id: Number(
                                                formik.values.work_order_id
                                              ),
                                            }
                                          : null
                                    }
                                    project={project || deviation?.project}
                                    extraFilters={workOrderPickFilters}
                                    {...formik}
                                  />
                                </div>
                              )}

                              {formik.values.work_order_link_mode ===
                                "create" && (
                                <div className="mt-4 space-y-4">
                                  <p className="text-sm text-slate-600">
                                    {t(
                                      "deviation.form.work_order_create_timing_intro"
                                    )}
                                  </p>
                                  <div className="flex flex-wrap gap-2">
                                    <button
                                      type="button"
                                      onClick={() =>
                                        formik.setFieldValue(
                                          "work_order_create_timing",
                                          "perform_now"
                                        )
                                      }
                                      className={`rounded-full px-3 py-1.5 text-sm font-medium transition ${
                                        formik.values
                                          .work_order_create_timing ===
                                        "perform_now"
                                          ? "bg-emerald-600 text-white shadow-sm"
                                          : "border border-slate-200 bg-white text-slate-700 hover:bg-slate-100"
                                      }`}
                                    >
                                      {t(
                                        "deviation.form.work_order_perform_now"
                                      )}
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        formik.setFieldValue(
                                          "work_order_create_timing",
                                          "plan_later"
                                        )
                                      }
                                      className={`rounded-full px-3 py-1.5 text-sm font-medium transition ${
                                        formik.values
                                          .work_order_create_timing ===
                                        "plan_later"
                                          ? "bg-amber-600 text-white shadow-sm"
                                          : "border border-slate-200 bg-white text-slate-700 hover:bg-slate-100"
                                      }`}
                                    >
                                      {t(
                                        "deviation.form.work_order_plan_later"
                                      )}
                                    </button>
                                  </div>

                                  <p className="text-xs text-slate-500">
                                    {formik.values.work_order_create_timing ===
                                    "perform_now"
                                      ? t(
                                          "deviation.form.work_order_perform_now_help"
                                        )
                                      : t(
                                          "deviation.form.work_order_plan_later_help"
                                        )}
                                  </p>

                                  {formik.values.work_order_create_timing ===
                                    "perform_now" && (
                                    <div>
                                      <label className="mb-1 block text-sm font-medium text-gray-700">
                                        {t("deviation.form.executor_label")}
                                      </label>
                                      <button
                                        type="button"
                                        onClick={() =>
                                          setShowExecutorUserSelector(true)
                                        }
                                        className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-left text-sm text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
                                      >
                                        <div className="font-medium text-slate-900">
                                          {selectedExecutorUser?.displayTitle ||
                                            selectedExecutorUser?.name ||
                                            t("deviation.form.executor_pick")}
                                        </div>
                                        <div className="mt-1 text-xs text-slate-500">
                                          {t("deviation.form.executor_hint")}
                                        </div>
                                      </button>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>

                            <div className="col-span-6 sm:col-span-3">
                              <label className="mb-1 block text-sm font-medium text-gray-700">
                                {t("deviation.form.deviation_type")}
                              </label>
                              {(() => {
                                const selectedType = deviationTypeOptions.find(
                                  (opt) =>
                                    String(opt.value) ===
                                    String(formik.values.deviation_type_id)
                                );
                                return (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setShowDeviationTypeDialog(true)
                                    }
                                    className={`w-full rounded-2xl border px-4 py-3 text-left text-sm transition-colors ${
                                      selectedType
                                        ? "border-blue-200 bg-blue-50 text-blue-800 ring-1 ring-blue-100"
                                        : "border-slate-200 bg-slate-50 text-slate-700 hover:border-slate-300 hover:bg-slate-100"
                                    }`}
                                  >
                                    <span className="font-medium">
                                      {selectedType?.title ||
                                        t("deviation.form.select_deviation_type")}
                                    </span>
                                  </button>
                                );
                              })()}
                              {formik.errors.deviation_type_id &&
                                formik.touched.deviation_type_id && (
                                  <p className="mt-1 text-sm text-red-600">
                                    {formik.errors.deviation_type_id}
                                  </p>
                                )}
                            </div>

                            <div className="col-span-6 sm:col-span-3">
                              <label className="mb-1 block text-sm font-medium text-gray-700">
                                {t("deviation.form.responsible_user")}
                              </label>
                              <button
                                type="button"
                                onClick={() =>
                                  setShowResponsibleUserSelector(true)
                                }
                                className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-left text-sm text-slate-700 transition hover:border-slate-300 hover:bg-slate-100"
                              >
                                <div className="font-medium text-slate-900">
                                  {selectedResponsibleUser?.displayTitle ||
                                    selectedResponsibleUser?.name ||
                                    t("deviation.form.select_responsible_user")}
                                </div>
                                <div className="mt-1 text-xs text-slate-500">
                                  {selectedResponsibleUser?.email ||
                                    t("deviation.form.responsible_for_follow_up")}
                                </div>
                              </button>
                            </div>

                            <div className="col-span-6 sm:col-span-3">
                              <label className="mb-1 block text-sm font-medium text-gray-700">
                                {t("deviation.form.severity")}
                              </label>
                              {(() => {
                                const severity = severityOptions.find(
                                  (opt) =>
                                    String(opt.value) ===
                                    String(formik.values.severity)
                                );
                                return (
                                  <button
                                    type="button"
                                    onClick={() => setShowSeverityDialog(true)}
                                    className={`w-full rounded-2xl border px-4 py-3 text-left text-sm font-medium transition-colors ${
                                      severity
                                        ? `${severity.class} border-slate-200 ring-1 ring-slate-100`
                                        : "border-slate-200 bg-slate-50 text-slate-700 hover:border-slate-300 hover:bg-slate-100"
                                    }`}
                                  >
                                    {severity?.label || t("deviation.form.select_severity")}
                                  </button>
                                );
                              })()}
                              {formik.errors.severity &&
                                formik.touched.severity && (
                                  <p className="mt-1 text-sm text-red-600">
                                    {formik.errors.severity}
                                  </p>
                                )}
                            </div>

                            <div className="col-span-6 sm:col-span-3">
                              <label className="mb-1 block text-sm font-medium text-gray-700">
                                {t("deviation.priority")}
                              </label>
                              {(() => {
                                const priority = priorityOptions.find(
                                  (opt) =>
                                    String(opt.value) ===
                                    String(formik.values.priority)
                                );
                                return (
                                  <button
                                    type="button"
                                    onClick={() => setShowPriorityDialog(true)}
                                    className={`w-full rounded-2xl border px-4 py-3 text-left text-sm font-medium transition-colors ${
                                      priority
                                        ? `${priority.class} border-slate-200 ring-1 ring-slate-100`
                                        : "border-slate-200 bg-slate-50 text-slate-700 hover:border-slate-300 hover:bg-slate-100"
                                    }`}
                                  >
                                    {priority?.label || t("deviation.form.select_priority")}
                                  </button>
                                );
                              })()}
                              {formik.errors.priority &&
                                formik.touched.priority && (
                                  <p className="mt-1 text-sm text-red-600">
                                    {formik.errors.priority}
                                  </p>
                                )}
                            </div>
                          </div>
                        </div>
                      </div>

                      <FormikSaveBar
                        {...formik}
                        isDirty={!formik.values.id || formik.dirty}
                        dockClassName="bottom-0 z-50 pb-2 sm:pb-5 max-lg:bottom-[4.75rem]"
                      />
                    </div>
                  </div>
                </Form>
              );
            }}
          </Formik>
        )}
      </DashboardHeader>

      {/* Severity Selection Dialog */}
      {showSeverityDialog && createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div
            className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
            onClick={() => setShowSeverityDialog(false)}
            aria-hidden="true"
          />
          <div
            className="relative w-full max-w-md overflow-hidden rounded-[1.5rem] bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="border-b border-slate-200 px-5 py-4">
              <div className="flex items-center justify-between gap-4">
                <h3 className="text-base font-semibold text-slate-900">
                  {t("deviation.form.select_severity")}
                </h3>
                <button
                  type="button"
                  onClick={() => setShowSeverityDialog(false)}
                  className="rounded-md p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="max-h-[24rem] space-y-2 overflow-y-auto px-3 py-3">
              {severityOptions.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => {
                    if (formikRef.current) {
                      formikRef.current.setFieldValue("severity", opt.value);
                      formikRef.current.setTouched({
                        ...formikRef.current.touched,
                        severity: true,
                      });
                    }
                    setShowSeverityDialog(false);
                  }}
                  className={`w-full rounded-2xl px-4 py-3 text-left text-sm font-medium transition-colors ${
                    String(formikRef.current?.values?.severity) === String(opt.value)
                      ? `${opt.class} ring-1 ring-slate-200`
                      : "bg-slate-50 text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Priority Selection Dialog */}
      {showPriorityDialog && createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div
            className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
            onClick={() => setShowPriorityDialog(false)}
            aria-hidden="true"
          />
          <div
            className="relative w-full max-w-md overflow-hidden rounded-[1.5rem] bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="border-b border-slate-200 px-5 py-4">
              <div className="flex items-center justify-between gap-4">
                <h3 className="text-base font-semibold text-slate-900">
                  {t("deviation.form.select_priority")}
                </h3>
                <button
                  type="button"
                  onClick={() => setShowPriorityDialog(false)}
                  className="rounded-md p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="max-h-[24rem] space-y-2 overflow-y-auto px-3 py-3">
              {priorityOptions.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => {
                    if (formikRef.current) {
                      formikRef.current.setFieldValue("priority", opt.value);
                      formikRef.current.setTouched({
                        ...formikRef.current.touched,
                        priority: true,
                      });
                    }
                    setShowPriorityDialog(false);
                  }}
                  className={`w-full rounded-2xl px-4 py-3 text-left text-sm font-medium transition-colors ${
                    String(formikRef.current?.values?.priority) === String(opt.value)
                      ? `${opt.class} ring-1 ring-slate-200`
                      : "bg-slate-50 text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Deviation Type Selection Dialog */}
      {showDeviationTypeDialog && createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div
            className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
            onClick={() => {
              setDeviationTypeSearch("");
              setShowDeviationTypeDialog(false);
            }}
            aria-hidden="true"
          />
          <div
            className="relative w-full max-w-lg overflow-hidden rounded-[1.5rem] bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="border-b border-slate-200 px-5 py-4">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <h3 className="text-base font-semibold text-slate-900">
                    {t("deviation.form.select_deviation_type")}
                  </h3>
                  <p className="mt-1 text-sm text-slate-500">
                    {t("deviation.form.type_category_hint")}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setDeviationTypeSearch("");
                    setShowDeviationTypeDialog(false);
                  }}
                  className="rounded-md p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
              <div className="relative mt-4">
                <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={deviationTypeSearch}
                  onChange={(e) => setDeviationTypeSearch(e.target.value)}
                  placeholder={t("deviation.form.search_types")}
                  className="h-11 w-full rounded-2xl border border-slate-200 bg-slate-50 pl-10 pr-3 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="max-h-[26rem] overflow-y-auto px-3 py-3">
              {deviationTypeOptions.length === 0 ? (
                <div className="px-3 py-8 text-center text-sm text-slate-500">
                  {t("hr.common.loading")}
                </div>
              ) : filteredDeviationTypeOptions.length === 0 ? (
                <div className="px-3 py-8 text-center text-sm text-slate-500">
                  {t("deviation.form.no_matching_types")}
                </div>
              ) : (
                filteredDeviationTypeOptions.map((opt) => {
                  const isSelected =
                    String(formikRef.current?.values?.deviation_type_id) ===
                    String(opt.value);
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => {
                        if (formikRef.current) {
                          formikRef.current.setFieldValue(
                            "deviation_type_id",
                            opt.value
                          );
                          formikRef.current.setTouched({
                            ...formikRef.current.touched,
                            deviation_type_id: true,
                          });
                        }
                        setDeviationTypeSearch("");
                        setShowDeviationTypeDialog(false);
                      }}
                      className={`mb-2 flex w-full items-center justify-between rounded-2xl px-4 py-3 text-left text-sm transition ${
                        isSelected
                          ? "bg-blue-50 font-medium text-blue-800 ring-1 ring-blue-200"
                          : "bg-slate-50 text-slate-700 hover:bg-slate-100"
                      }`}
                    >
                      <span>{opt.title}</span>
                      {isSelected ? (
                        <span className="rounded-full bg-blue-100 px-2 py-1 text-xs font-medium text-blue-700">
                          {t("deviation.form.selected")}
                        </span>
                      ) : null}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {showResponsibleUserSelector && createPortal(
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
          <div 
            className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
            onClick={() => setShowResponsibleUserSelector(false)}
            aria-hidden="true"
          />
          <div
            className="relative w-full max-w-lg overflow-hidden rounded-[1.5rem] bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="border-b border-slate-200 px-5 py-4">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <h3 className="text-base font-semibold text-slate-900">{t("deviation.form.select_responsible_user")}</h3>
                  <p className="mt-1 text-sm text-slate-500">{t("deviation.form.choose_who_should_own_this_deviation")}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowResponsibleUserSelector(false)}
                  className="rounded-md p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
              <div className="relative mt-4">
                <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={employeeSearch}
                  onChange={(e) => setEmployeeSearch(e.target.value)}
                  placeholder={t("deviation.form.search_employees")}
                  className="h-11 w-full rounded-2xl border border-slate-200 bg-slate-50 pl-10 pr-3 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="max-h-[26rem] overflow-y-auto px-3 py-3">
              {employeeState.isLoading ? (
                <div className="px-3 py-8 text-center text-sm text-slate-500">{t("hr.common.loading")}</div>
              ) : filteredEmployees.length === 0 ? (
                <div className="px-3 py-8 text-center text-sm text-slate-500">{t("deviation.form.no_employees_found")}</div>
              ) : (
                filteredEmployees.map((employee) => {
                  const isSelected =
                    String(employee.id) === String(formikRef.current?.values?.responsible_user_id);

                  return (
                    <button
                      key={employee.id}
                      type="button"
                      onClick={() => {
                        if (formikRef.current) {
                          formikRef.current.setFieldValue("responsible_user_id", employee.id);
                        }
                        setShowResponsibleUserSelector(false);
                      }}
                      className={`mb-2 flex w-full items-start justify-between rounded-2xl px-4 py-3 text-left transition ${
                        isSelected
                          ? "bg-blue-50 text-blue-700 ring-1 ring-blue-200"
                          : "bg-slate-50 text-slate-700 hover:bg-slate-100"
                      }`}
                    >
                      <div>
                        <div className="font-medium">{employee.displayTitle || employee.name}</div>
                        <div className="mt-1 text-xs text-slate-500">{employee.email || t("deviation.form.no_email")}</div>
                      </div>
                      {isSelected ? (
                        <span className="rounded-full bg-blue-100 px-2 py-1 text-xs font-medium text-blue-700">
                          {t("deviation.form.selected")}
                        </span>
                      ) : null}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {showExecutorUserSelector &&
        createPortal(
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => setShowExecutorUserSelector(false)}
              aria-hidden="true"
            />
            <div
              className="relative w-full max-w-lg overflow-hidden rounded-[1.5rem] bg-white shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="border-b border-slate-200 px-5 py-4">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <h3 className="text-base font-semibold text-slate-900">
                      {t("deviation.form.executor_label")}
                    </h3>
                    <p className="mt-1 text-sm text-slate-500">
                      {t("deviation.form.executor_dialog_help")}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowExecutorUserSelector(false)}
                    className="rounded-md p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
                <div className="relative mt-4">
                  <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    value={executorUserSearch}
                    onChange={(e) => setExecutorUserSearch(e.target.value)}
                    placeholder={t("deviation.form.search_employees")}
                    className="h-11 w-full rounded-2xl border border-slate-200 bg-slate-50 pl-10 pr-3 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="max-h-[26rem] overflow-y-auto px-3 py-3">
                {employeeState.isLoading ? (
                  <div className="px-3 py-8 text-center text-sm text-slate-500">
                    {t("hr.common.loading")}
                  </div>
                ) : filteredExecutorEmployees.length === 0 ? (
                  <div className="px-3 py-8 text-center text-sm text-slate-500">
                    {t("deviation.form.no_employees_found")}
                  </div>
                ) : (
                  filteredExecutorEmployees.map((employee) => {
                    const isSelected =
                      String(employee.id) ===
                      String(formikRef.current?.values?.work_order_executor_user_id);

                    return (
                      <button
                        key={employee.id}
                        type="button"
                        onClick={() => {
                          if (formikRef.current) {
                            formikRef.current.setFieldValue(
                              "work_order_executor_user_id",
                              employee.id
                            );
                          }
                          setExecutorUserSearch("");
                          setShowExecutorUserSelector(false);
                        }}
                        className={`mb-2 flex w-full items-start justify-between rounded-2xl px-4 py-3 text-left transition ${
                          isSelected
                            ? "bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200"
                            : "bg-slate-50 text-slate-700 hover:bg-slate-100"
                        }`}
                      >
                        <div>
                          <div className="font-medium">
                            {employee.displayTitle || employee.name}
                          </div>
                          <div className="mt-1 text-xs text-slate-500">
                            {employee.email || t("deviation.form.no_email")}
                          </div>
                        </div>
                        {isSelected ? (
                          <span className="rounded-full bg-emerald-100 px-2 py-1 text-xs font-medium text-emerald-800">
                            {t("deviation.form.selected")}
                          </span>
                        ) : null}
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

